import { useEffect } from "react";

/**
 * Reads Search Console / Bing verification codes + optional analytics IDs
 * from REACT_APP_* env vars at build time. Defaults are empty strings so
 * nothing is injected unless explicitly set — safe for preview builds.
 *
 * To activate post-deploy, set in frontend/.env (no redeploy required for
 * dev preview, but a build is required for production):
 *
 *   REACT_APP_GSC_VERIFICATION=abc123…   (Google Search Console)
 *   REACT_APP_BING_VERIFICATION=xyz789…   (Bing Webmaster)
 *   REACT_APP_GA4_ID=G-XXXXXXXXXX         (optional)
 *   REACT_APP_PLAUSIBLE_DOMAIN=easyquran.uk  (optional, alt to GA4)
 */

const GSC = process.env.REACT_APP_GSC_VERIFICATION || "";
const BING = process.env.REACT_APP_BING_VERIFICATION || "";
const GA4 = process.env.REACT_APP_GA4_ID || "";
const PLAUSIBLE = process.env.REACT_APP_PLAUSIBLE_DOMAIN || "";

const upsertMeta = (name, content) => {
  if (!content) return;
  let el = document.head.querySelector(`meta[name="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("name", name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
};

const injectScript = (id, src, attrs = {}, inline = "") => {
  if (document.getElementById(id)) return;
  const s = document.createElement("script");
  s.id = id;
  if (src) s.src = src;
  Object.entries(attrs).forEach(([k, v]) => s.setAttribute(k, v));
  if (inline) s.textContent = inline;
  document.head.appendChild(s);
};

/**
 * One-time, app-shell mount. Adds:
 *  - Search-engine verification meta tags (Google + Bing)
 *  - GA4 gtag.js loader  (only if REACT_APP_GA4_ID is set)
 *  - Plausible script   (only if REACT_APP_PLAUSIBLE_DOMAIN is set)
 */
export function useSearchVerification() {
  useEffect(() => {
    upsertMeta("google-site-verification", GSC);
    upsertMeta("msvalidate.01", BING);

    if (GA4) {
      injectScript(`ga4-loader-${GA4}`, `https://www.googletagmanager.com/gtag/js?id=${GA4}`, { async: "" });
      injectScript(
        "ga4-inline",
        null,
        {},
        `window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}gtag('js',new Date());gtag('config','${GA4}');`
      );
    }

    if (PLAUSIBLE) {
      injectScript(
        "plausible-script",
        "https://plausible.io/js/script.js",
        { defer: "", "data-domain": PLAUSIBLE }
      );
    }
  }, []);
}
